<template>
  <div>
    <page-header
      left-side-class="col-xl-4"
      :page="pageInfo"
      right-side-class="col-xl-8 align-items-end d-flex pb-2 justify-content-end"
    >
      <template #customActions>
        <action-button
          class="mr-3"
          i="fas fa-arrow-circle-left"
          text="Regresar"
          variant="outline-success"
          @click="goBackHandler()"
        />
        <action-button
          v-if="!showSearchStudent && !isEducativeService"
          class="mr-3"
          i="fas fa-search"
          text="Búsqueda de Estudiante"
          variant="outline-success"
          @click="showSearchStudent = true"
        />
        <action-button
          v-if="!isPeriodFinalized && !readonly && !isEducativeService"
          i="fas fa-check-circle"
          text="Validar Visitas"
          variant="success"
          @click="validateVisitHandler"
        />
      </template>
    </page-header>

    <validation-observer ref="searchStudentRef">
      <div
        v-if="showSearchStudent"
        class="bg-gray-1 p-4 rounded mb-4 search-container"
      >
        <div class="search-container__search">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="referralNumberOrSie"
              class="input w-25"
              custom-error-msg="Favor escriba número de referido o SIE."
              :error="errors[0]"
              icon="fa fa-search orange-icon"
              left-icon
              placeholder="Escriba Número de Referido o SIE"
            />
          </validation-provider>
          <action-button
            class="mr-n3 ml-0 ml-md-3 ml-sm-0 mt-3 mt-md-0 mt-sm-3"
            i="fas fa-search"
            text="Buscar"
            variant="success"
            @click="searchStudent"
          />
        </div>
        <action-button
          class="mr-n2 px-3 mt-1 mt-md-0 mt-sm-1"
          i="fas fa-times-circle"
          text="Cerrar"
          variant="outline-danger"
          @click="onCloseSearchStudent"
        />
      </div>
    </validation-observer>

    <custom-result-not-found
      v-if="!studentInfo"
      text="No se ha encontrado información disponible"
    />
    <div v-if="studentInfo && showContent">
      <additional-info
        :compensatory-info="compensatoryInfo"
        :referral-count="referralCount"
        :referral-current-page="referralCurrentPage"
        :referrals="referrals"
        :student-info="studentInfo"
        @on-change-page="onChangeNavigationPageHandler"
      />
      <student-intervention-plan-info
        v-if="!isEducativeService"
        v-model="interventionPlanList"
        :has-plan="hasPlan"
        :student-info="studentInfo"
        @on-view-document="viewDocument($event)"
        @on-view-plan="onViewPlanHandler"
      />
      <student-pei-info v-if="isEducativeService" :value="studentPeiInfoData" />
      <div>
        <div class="separation-line" />
        <div class="row mt-3">
          <div class="col">
            <period-selection
              :completed-period="getIsPeriodFinalizedDate"
              :current-period="getCurrentPeriod"
              :disabled-periods="getDisabledDates"
              :disabled-regular-period="compensatoryInfo != null"
              :initial-period="getInitialPeriod"
              :readonly="readonly"
              @input="onChangePeriod"
            />
          </div>
        </div>
        <div v-if="!isEducativeService" class="row mt-5">
          <div v-if="referrals.length > 0" class="col">
            <register-visit
              :key="registerVisitKey"
              :compensatory-info="compensatoryInfo"
              :is-period-finalized="isPeriodFinalized"
              :is-read-only="readonly"
              :referral-for-visits="referrals"
              :student-info="studentInfo"
              @hook:mounted="runValidations"
            />
          </div>
        </div>
      </div>
      <register-education-visit
        v-if="isEducativeService"
        class="mt-2"
        :compensatory-info="compensatoryInfo"
        :is-period-finalized="isPeriodFinalized"
        :referral-for-visits="referrals"
        :student-info="studentInfo"
        @reloadVisitTypes="loadVisitTypes"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import {
  AdditionalInfo,
  PeriodSelection,
  RegisterVisit,
  StudentInterventionPlanInfo,
  StudentPeiInfo,
  RegisterEducationVisit,
} from './components';
import CustomInput from 'CustomInput';
import CustomResultNotFound from 'custom-result-not-found';
import { name } from '@/modules/visitRegistry/store';
import {
  ACTION_TYPES,
  MUTATION_TYPES,
} from '@/modules/visitRegistry/store/types';
import { createNamespacedHelpers } from 'vuex';
const {
  mapState,
  mapMutations,
  mapActions,
  mapGetters,
} = createNamespacedHelpers(name);
import _visitRegistryApi from '@/modules/visitRegistry/api.js';
import _generalApi from '@/modules/app/api.js';
import alertMixin from '@/mixins/alert.mixin.js';
import roles from '@/utils/constants/roles.js';

const INTERVENTION_PLAN_ROUTE_NAME = 'interventionPlan';

export default {
  name: 'VisitValidation',
  components: {
    PageHeader,
    ActionButton,
    AdditionalInfo,
    CustomInput,
    CustomResultNotFound,
    PeriodSelection,
    RegisterVisit,
    StudentInterventionPlanInfo,
    StudentPeiInfo,
    RegisterEducationVisit,
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('DD/MMM/YYYY');
      }
    },
  },
  mixins: [alertMixin],
  data: () => ({
    registerVisitKey: null,
    readonly: false,
    studentInfo: {},
    studentInfoOriginal: {},
    compensatoryInfo: null,
    interventionPlanList: [],
    extraExpanded: false,
    referrals: [],
    referralsOriginal: [],
    referralCount: 0,
    referralCurrentPage: 1,
    periodSelectorCurrent: null,
    referralNumberOrSie: null,
    showSearchStudent: null,
    studentPeiInfoData: [],
    showContent: false,
  }),
  computed: {
    ...mapGetters({
      selectedPeriodFiltered: 'getFilteredPeriod',
    }),
    ...mapState({
      isPeriodFinalized: (state) => state.isPeriodFinalized,
    }),
    selectedPeriod() {
      let x = this.selectedPeriodFiltered?.year
        ? this.selectedPeriodFiltered
        : this.studentInfoOriginal.currentPeriod;
      return x;
    },
    selectedPeriodDate() {
      return moment()
        .year(this.selectedPeriod.year)
        .month(this.selectedPeriod.monthNumber - 1)
        .date(1)
        .toDate();
    },
    pageInfo() {
      return {
        title: 'Registro de Visitas',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Registro de Visita'],
      };
    },
    getStatusColor() {
      return (status) => {
        if (status === 'Sometido') {
          return '#A4E5B2';
        }
        return '#838383';
      };
    },
    getCurrentPeriod() {
      return new Date(
        this.selectedPeriod?.year,
        this.selectedPeriod?.monthNumber,
        0
      );
    },
    getInitialPeriod() {
      const year = this.studentInfoOriginal.initialPeriod
        ? this.studentInfoOriginal.initialPeriod.year
        : this.studentInfoOriginal.initialPeriodInfo?.year;

      const month = this.studentInfoOriginal.initialPeriod
        ? this.studentInfoOriginal.initialPeriod.monthNumber
        : this.studentInfoOriginal.initialPeriodInfo?.monthNumber;

      return new Date(year, month, 0);
    },
    getIsPeriodFinalizedDate() {
      return this.isPeriodFinalized ? this.selectedPeriodDate : null;
    },
    getDisabledDates() {
      let stopDate = this.currentAcademicYear().toDate;
      var dateArray = [];
      var originalDate = moment(new Date());
      var currentDate = this.isEducativeService
        ? moment(new Date()).add(1, 'month')
        : moment(new Date());
      var stopDateVal = moment(stopDate).add(1, 'month');
      const effectiveDateFrom =
        this.studentInfo.effectiveDateFrom ??
        this.studentInfo.assignmentStartDate;
      const effectiveDateTo =
        this.studentInfo.effectiveDateTo ?? this.studentInfo.assignmentEndDate;

      while (currentDate < stopDateVal) {
        const fromDay = moment(effectiveDateFrom, 'DD/MMM/YYYY').set('date', 1);
        const toDay = moment(effectiveDateTo, 'DD/MMM/YYYY')
          .set('date', 1)
          .add(1, 'month');
        if (!moment(currentDate).isBetween(fromDay, toDay)) {
          dateArray.push(moment(currentDate).toDate());
        } else if (currentDate > originalDate) {
          dateArray.push(moment(currentDate).toDate());
        }
        currentDate = moment(currentDate).add(1, 'month');
      }

      return dateArray;
    },
    isSIE() {
      return (value) => value?.length <= 8 ?? null;
    },
    isProvider() {
      return [roles.Provider, roles.AsistenteProveedor].includes(this.userRole);
    },
    hasPlan() {
      return (
        this.interventionPlanList.length > 0 ||
        this.studentInfo?.isPlanSubmitted
      );
    },
    isEducativeService() {
      return this.studentInfo?.isEducativeService;
    },
    navParams() {
      return {
        from: this.$route.params.from,
        fromData: this.$route.params.fromData,
      };
    },
    disciplineTypeId() {
      return this.studentInfo?.disciplineTypeId;
    },
  },
  async mounted() {
    const visitInfo = this.$route.params;
    if (visitInfo) {
      this.readonly = visitInfo.readonly;
      this.studentInfo = visitInfo;
      this.studentInfoOriginal = visitInfo;
      this.compensatoryInfo = visitInfo.compensatoryInfo;

      await this.callGetFinalizedPeriod();
      //reset selected period
      this[MUTATION_TYPES.SET_SELECTED_PERIOD](null);
      await this.onLoadReferralInfo(visitInfo);
    }

    this.loadVisitTypes();

    if (visitInfo.isEducativeService) {
      await this.onLoadStudentPeiInfo(visitInfo.studentSieId);
    }
    this.showContent = true;
  },
  methods: {
    ...mapMutations([MUTATION_TYPES.SET_SELECTED_PERIOD]),
    ...mapActions([
      ACTION_TYPES.GET_VISIT_TYPES,
      ACTION_TYPES.GET_EDUCATION_VISIT_TYPES,
      ACTION_TYPES.GET_FINALIZED_PERIOD,
      ACTION_TYPES.SEARCH,
      ACTION_TYPES.VISIT_VALIDATION_INFO,
    ]),
    loadVisitTypes() {
      const visitInfo = this.$route.params;

      if (this.isEducativeService) {
        this[ACTION_TYPES.GET_EDUCATION_VISIT_TYPES]({
          requestTypeId: this.compensatoryInfo?.requestTypeId ?? null,
          disciplineTypeId: this.disciplineTypeId,
          studentSieId: visitInfo.studentSieId,
        });
      } else {
        this[ACTION_TYPES.GET_VISIT_TYPES]({
          requestTypeId: this.compensatoryInfo?.requestTypeId ?? null,
          disciplineTypeId: this.disciplineTypeId,
          studentSieId: visitInfo.studentSieId,
        });
      }
    },
    async onLoadReferralInfo(visitInfo) {
      let filters = visitInfo.filter ?? null;
      let visitFilterObject = {};

      if (filters != null) {
        const specialistId = !filters.supervisedSpecialist?.id
          ? filters.specialist?.id
          : filters.supervisedSpecialist?.id;
        const studentSieId = filters?.sie?.trim();
        const isSie = this.isSIE(studentSieId);
        visitFilterObject = {
          academicYearId: filters.academicYear.id,
          serviceDate: filters.month?.id,
          companyId: filters.provider?.id,
          specialistId: specialistId,
          disciplineTypeId: filters.service?.id,
          centerId: filters.center?.id,
          locationTypeId: filters.locationType?.id,
          locationFiscalYearId: filters.location?.id,
          studentAge: filters.age?.id,
          Action: filters.action?.id,
          studentSieId: isSie ? studentSieId : null,
          referralNumber: !isSie ? studentSieId : null,
          requestTypeId: this.compensatoryInfo?.requestTypeId,
          pageIndex: 1,
          pageSize: 0, //0 = all
        };
      } else {
        visitFilterObject = {
          ...visitInfo,
          requestTypeId: this.compensatoryInfo?.requestTypeId,
        };
        visitFilterObject.admissionId = null;
      }

      const result = await _visitRegistryApi.search(visitFilterObject);
      if (result) {
        this.referrals = result.data;
        this.referralsOriginal = result.data;

        this.referralCurrentPage =
          result.data.findIndex(
            (x) => x.studentSieId === visitInfo.studentSieId
          ) + 1;
        this.referralCount = result.data.length;

        if (this.referralCount > 0) {
          await this.onChangeNavigationPageHandler();
        }
      }
    },
    async onLoadInterventionPlan(visitStudentServiceId) {
      const { data: result } = await _visitRegistryApi.getInterventionPlan(
        visitStudentServiceId
      );
      if (result) {
        this.interventionPlanList = result;
        if (this.interventionPlanList.length == 0) {
          this.extraExpanded = true;
        }
      }
    },
    async viewDocument({ documentId }) {
      if (documentId) {
        await _generalApi.downloadDocumentByAttachmentId(documentId);
      }
    },
    onViewPlanHandler() {
      this.$router.push({
        name: INTERVENTION_PLAN_ROUTE_NAME,
        params: {
          from: 'VisitRegistry',
          admissionId: this.studentInfo.admissionId,
          academicYearId: this.studentInfo.academicYearId,
          interventionPlanId: this.studentInfo.planId,
        },
      });
    },
    async runValidations() {
      if (this.studentInfoOriginal) {
        await this.validateVisitHandler(true);
      }
    },
    async validateVisitHandler(noToast) {
      const validationResult = await this[ACTION_TYPES.VISIT_VALIDATION_INFO]({
        studentServiceId: this.studentInfoOriginal.visitStudentServiceId,
        academicYearId: this.studentInfoOriginal.academicYearId,
        monthDate: this.selectedPeriodDate,
        centerId: this.studentInfoOriginal.centerId,
      });

      if (noToast) return;

      let toastMessage =
        validationResult.errorCount > 0
          ? 'presenta errores'
          : 'se completó exitosamente';
      this.ShowToast(
        'Proceso exitoso.',
        `El proceso de validación ${toastMessage}. Se identificaron ${validationResult.errorCount} error(es)`,
        validationResult.errorCount > 0 ? 'error' : 'success'
      );
    },
    getIcon(expanded) {
      return expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right';
    },
    getBgIcon(expanded) {
      return expanded ? 'green' : 'green';
    },
    toggleVisibility() {},
    async onChangePeriod(_period) {
      if (_period) {
        //update selected period
        this[MUTATION_TYPES.SET_SELECTED_PERIOD](_period);

        //load referrals
        await this.onLoadReferralInfo({
          ...this.studentInfoOriginal,
          serviceDate: moment()
            .set({
              day: 1,
              month: _period.monthNumber - 1,
              year: _period.year,
            })
            .format('MMMM YYYY'),
        });

        //get finalized period
        await this.callGetFinalizedPeriod();

        await this.validateVisitHandler(true);
      }
    },
    async callGetFinalizedPeriod() {
      await this[ACTION_TYPES.GET_FINALIZED_PERIOD]({
        academicYearId: this.studentInfoOriginal.academicYearId,
        month: this.selectedPeriodDate,
        specialistId: this.studentInfoOriginal.specialistId,
        companyId: this.studentInfoOriginal.companyId,
        centerId: this.studentInfoOriginal.centerId,
      });
    },
    async searchStudent() {
      const isValid = await this.$refs.searchStudentRef.validate();
      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }

      this.studentInfoOriginal.filter.sie = this.referralNumberOrSie;

      await this.onLoadReferralInfo(this.studentInfoOriginal);
      this.registerVisitKey = this.studentInfoOriginal.filter.sie;
    },
    async onCloseSearchStudent() {
      this.showSearchStudent = null;
      this.referralNumberOrSie = null;
      this.studentInfo = { ...this.studentInfoOriginal };
    },
    async onChangeNavigationPageHandler(page = this.referralCurrentPage) {
      const prevPage = this.referralCurrentPage;
      const isForward = page > prevPage;

      this.referralCurrentPage = page > 0 ? page - 1 : 0;

      // Move in the proper direction until we find a student with a plan
      while (
        this.referralCurrentPage >= 0 &&
        this.referralCurrentPage < this.referralsOriginal.length &&
        !this.referralsOriginal[this.referralCurrentPage].hasPlan
      ) {
        this.referralCurrentPage += isForward ? 1 : -1;
      }

      if (
        this.referralCurrentPage < 0 ||
        this.referralCurrentPage >= this.referralsOriginal.length
      ) {
        return;
      }

      this.referrals = this.referralsOriginal.filter(
        (_, index) => this.referralCurrentPage === index
      );
      this.studentInfo = this.referralsOriginal[this.referralCurrentPage];
      this.registerVisitKey = this.studentInfo.studentSieId;

      await this.onLoadInterventionPlan(this.studentInfo.visitStudentServiceId);
    },
    async onLoadStudentPeiInfo(studentSieId) {
      const { data: result } = await _visitRegistryApi.getStudentPeiInfo(
        studentSieId
      );
      if (result) {
        this.studentPeiInfoData = result;
        if (this.studentPeiInfoData.length == 0) this.extraExpanded = true;
      }
    },
    async goBackHandler() {
      if (this.navParams.from) {
        this.$router.push({
          name: this.navParams.from,
          params: this.navParams.fromData,
        });
        return;
      }
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';
.icon-toggle {
  display: flex;
  align-items: center;
  border-radius: 32px;
  padding: 5px;
  display: inline-block;
  background: #f2f2f2;
  padding-left: 5px;
  padding-right: 20px;

  .dot-title {
    font: Bold 18px/22px Lato;
    letter-spacing: 0px;
    color: #11291b;
  }

  .title-icon {
    color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 7px;
  }
}

.dot {
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: #fff;
  display: inline-block;
  margin-right: 15px;
}

.dot.green {
  background-color: #2db782;
}
.dot.gray {
  background-color: #3f5b60;
}

.plan-container {
  background: #f7f7f7;
  padding: 10px;
  border-radius: 5px;

  &__row {
    margin: 8px 5px;
  }
}

.legend {
  text-align: left;
  font: Regular 14px/28px Lato;
  letter-spacing: 0px;
  color: #727272;
}

.transparent-color:hover {
  color: #2db782 !important;
}

.date-spacing {
  margin-left: 56px;
}
.separation-line {
  border-bottom: 3px solid map-get($colors, 'gray-4');
  margin-top: 30px;
}

.separator-line-2 {
  border-bottom: 1px solid #e2e2e2;
}

.search-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  &__search {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex: 1;
    .input {
      min-width: 21.875rem;
    }
  }
}

@media only screen and (max-width: 768px) {
  .search-container {
    display: flex;
    flex-direction: column;
    &__search {
      display: flex;
      flex-direction: column;
      flex: 1;
      .input {
        flex: 1;
        min-width: 100% !important;
      }
    }
  }
}
</style>
